/** Per-project module store cart (localStorage). */

const CART_VERSION = 'v1';

function cartKey(slug) {
  return `webscada.moduleStore.cart.${CART_VERSION}:${slug}`;
}

export function emptyCart() {
  return { modules: [], quotas: {} };
}

export function loadCart(slug) {
  if (!slug) return emptyCart();
  try {
    const raw = localStorage.getItem(cartKey(slug));
    if (!raw) return emptyCart();
    const parsed = JSON.parse(raw);
    return {
      modules: Array.isArray(parsed.modules) ? parsed.modules : [],
      quotas: parsed.quotas && typeof parsed.quotas === 'object' ? parsed.quotas : {},
    };
  } catch {
    return emptyCart();
  }
}

export function saveCart(slug, cart) {
  if (!slug) return;
  localStorage.setItem(
    cartKey(slug),
    JSON.stringify({
      modules: cart.modules || [],
      quotas: cart.quotas || {},
      updatedAt: new Date().toISOString(),
    }),
  );
}

export function addModuleToCart(slug, moduleCode) {
  const cart = loadCart(slug);
  if (!cart.modules.includes(moduleCode)) {
    cart.modules = [...cart.modules, moduleCode];
    saveCart(slug, cart);
  }
  return cart;
}

export function removeModuleFromCart(slug, moduleCode) {
  const cart = loadCart(slug);
  cart.modules = cart.modules.filter((c) => c !== moduleCode);
  saveCart(slug, cart);
  return cart;
}

export function setQuotaInCart(slug, quotaCode, option) {
  const cart = loadCart(slug);
  if (!option) {
    delete cart.quotas[quotaCode];
  } else {
    cart.quotas[quotaCode] = option;
  }
  saveCart(slug, cart);
  return cart;
}

export function clearCart(slug) {
  saveCart(slug, emptyCart());
  return emptyCart();
}

export function cartItemCount(cart) {
  const moduleCount = cart?.modules?.length || 0;
  const quotaCount = Object.keys(cart?.quotas || {}).length;
  return moduleCount + quotaCount;
}

export function expandModuleDependencies(moduleCodes, registryModules) {
  const byCode = new Map((registryModules || []).map((m) => [m.code, m]));
  const result = new Set(moduleCodes || []);
  let changed = true;
  while (changed) {
    changed = false;
    for (const code of [...result]) {
      const def = byCode.get(code);
      for (const dep of def?.dependsOn || []) {
        if (!result.has(dep)) {
          result.add(dep);
          changed = true;
        }
      }
    }
  }
  return [...result];
}

export function formatCartRequestText({ slug, cart, catalog, locale }) {
  const lines = [
    `${locale.supportEmailSubject}: ${slug}`,
    '',
    'Modules:',
  ];
  for (const code of cart.modules) {
    const listing = catalog?.listings?.find((l) => l.code === code);
    const mod = catalog?.modules?.find((m) => m.code === code);
    const name = listing?.tagline || mod?.name || code;
    const price = listing?.price?.monthly;
    lines.push(`  - ${code}: ${name}${price != null ? ` ($${price}/mo)` : ''}`);
  }
  if (Object.keys(cart.quotas).length) {
    lines.push('', 'Quotas:');
    for (const [qCode, opt] of Object.entries(cart.quotas)) {
      lines.push(`  - ${qCode}: ${opt?.label || opt?.value} ($${opt?.priceMonthly || 0}/mo)`);
    }
  }
  lines.push('', `Project slug: ${slug}`);
  return lines.join('\n');
}
